import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    firstValueFrom,
    Observable,
    map,
    of,
    take
} from 'rxjs';
import { AuthApiService } from './auth-api.service';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { SessionData } from '../helper/auth.helper';
import { LoginResponse } from '../helper/dto/login-response.dto';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _sessionData = new BehaviorSubject<SessionData>(new SessionData());
    readonly sessionData = this._sessionData.asObservable();
    private sessionDataObject: SessionData | undefined;
    private isUpdatingSession: boolean = false;

    constructor(
        private authApiService: AuthApiService,
        private router: Router
    ) {
        // Check if there is a token in the local storage
        const savedSession = sessionStorage.getItem('shop_session');

        if (savedSession) {
            const session = new SessionData();
            const parsedData = jwtDecode(savedSession) as any;
            session.user._id = parsedData._id;
            session.user.name = parsedData.name;
            session.user.email = parsedData.mail;
            session.user.roles = parsedData.roles;
            session.user.active = parsedData.active;
            session.user.profile = parsedData.profile;
            session.user.language = parsedData.language
                ? parsedData.language
                : 'de';
            session.user.oidcIdentifier = parsedData.oidcIdentifier;
            session.tokenType = parsedData.type;
            session.user.expires = parsedData.expires;
            session.token = savedSession;
            session.user.isSystemAdmin = parsedData.isSystemAdmin;

            if (session.tokenType === 'twofactortoken') {
                session.require2Fa = true;
                session.isLoggedIn = false;

                if (parsedData.twofactors) {
                    session.twofactors = parsedData.twofactors;
                }
            } else {
                session.isLoggedIn = true;
                session.require2Fa = false;
            }

            if (session.tokenType === 'actortoken') {
                session.actor = parsedData.actor;
            }

            this._sessionData.next(session);
        }
    }

    public async login(
        username: string,
        password: string
    ): Promise<LoginResponse> {
        const result = await firstValueFrom(
            this.authApiService.login(username, password)
        );
        sessionStorage.setItem('shop_session', result.data.token);

        const session = new SessionData();
        if (result.message === 'TwoFactor is required!') {
            session.isLoggedIn = false;
            session.require2Fa = true;
            session.stage = 'twofa';
        } else {
            session.isLoggedIn = true;
        }

        session.token = result.data.token;

        const parsedData = jwtDecode(session.token) as any;

        session.user.name = parsedData.name;
        session.user.email = parsedData.mail;
        session.user.roles = parsedData.roles;
        session.user.active = parsedData.active;
        session.tokenType = parsedData.type;
        session.user.expires = parsedData.expires;
        session.user.isSystemAdmin = parsedData.isSystemAdmin;
        session.user.oidcIdentifier = parsedData.oidcIdentifier;
        session.user.profile = parsedData.profile;

        this.sessionDataObject = session;
        this._sessionData.next(session);

        return result;
    }

    public async twoFactor(name: string, code: string): Promise<LoginResponse> {
        const result = await firstValueFrom(
            this.authApiService.twoFactorLogin(
                name,
                code,
                this._sessionData.value.token
            )
        );

        const session = this._sessionData.value;

        if (result.message === 'Login successfull!') {
            sessionStorage.setItem('shop_session', result.data.token);
            const parsedData = jwtDecode(result.data.token) as any;
            session.token = result.data.token;
            session.isLoggedIn = true;
            session.require2Fa = false;
            session.user.name = parsedData.name;
            session.user.email = parsedData.mail;
            session.user.roles = parsedData.roles;
            session.user.active = parsedData.active;
            session.tokenType = parsedData.type;
            session.user.expires = parsedData.expires;
            session.user.isSystemAdmin = parsedData.isSystemAdmin;
            session.user.oidcIdentifier = parsedData.oidcIdentifier;
            session.user.profile = parsedData.profile;
            this._sessionData.next(session);
        }

        return result;
    }

    async tokenBySession(): Promise<boolean> {
        this.isUpdatingSession = true;
        const result = await firstValueFrom(
            this.authApiService.tokenBySession()
        ).catch((error) => {
            this.isUpdatingSession = false;
            return undefined;
        });

        if (result && result.message === 'User token') {
            sessionStorage.setItem('shop_session', result.data.token);
            const session = new SessionData();
            session.token = result.data.token;
            const parsedData = jwtDecode(result.data.token) as any;
            session.user._id = parsedData._id;
            session.user.name = parsedData.name;
            session.user.email = parsedData.mail;
            session.user.roles = parsedData.roles;
            session.user.active = parsedData.active;
            session.tokenType = parsedData.type;
            session.user.expires = parsedData.expires;
            session.isLoggedIn = true;
            session.user.isSystemAdmin = parsedData.isSystemAdmin;
            session.user.oidcIdentifier = parsedData.oidcIdentifier;
            session.user.profile = parsedData.profile;
            this._sessionData.next(session);
            this.isUpdatingSession = false;
            return true;
        }

        this.isUpdatingSession = false;
        return false;
    }

    expired() {
        sessionStorage.removeItem('shop_session');
        this._sessionData.next(new SessionData());
    }

    logout(): Observable<boolean> {
        return this.authApiService.logout().pipe(
            catchError((error) => {
                console.error('Faled to call logout endpoint:', error);
                return of(null);
            }),
            map(() => {
                sessionStorage.removeItem('shop_session');
                this._sessionData.next(new SessionData());
                return true;
            })
        );
    }

    checkAndRenewToken(
        force: boolean = false,
        callback = (session?: SessionData) => { }
    ) {
        this._sessionData.pipe(take(1)).subscribe((data) => {
            if (data && data.user.expires !== 0) {
                // If session expires in less than 30 minutes, renew the token
                const now = new Date();
                const expires = new Date(data.user.expires);
                const diff = expires.getTime() - now.getTime();
                const diffInMinutes = diff / 60000;
                if (force || diffInMinutes < 30) {
                    console.log(
                        `The current token will expire in ${diffInMinutes} minutes. Renewing token...`
                    );
                    this.authApiService.renewToken(data.token).subscribe({
                        next: (result) => {
                            if (result.message === 'Login successfull!') {
                                sessionStorage.setItem(
                                    'shop_session',
                                    result.data.token
                                );
                                const session = new SessionData();
                                session.token = result.data.token;
                                const parsedData = jwtDecode(
                                    result.data.token
                                ) as any;
                                session.user._id = parsedData._id;
                                session.user.name = parsedData.name;
                                session.user.email = parsedData.mail;
                                session.user.roles = parsedData.roles;
                                session.user.active = parsedData.active;
                                session.tokenType = parsedData.type;
                                session.user.expires = parsedData.expires;
                                session.user.oidcIdentifier =
                                    parsedData.oidcIdentifier;
                                session.isLoggedIn = true;
                                session.user.isSystemAdmin =
                                    parsedData.isSystemAdmin;
                                session.actor =
                                    session.tokenType === 'actortoken'
                                        ? parsedData.actor
                                        : undefined;
                                session.user.profile = parsedData.profile;
                                this._sessionData.next(session);
                                callback(session);
                            } else {
                                this.expired();
                                this.router.navigate(['/login']);
                            }
                        },
                        error: (error) => {
                            console.error(error);
                            this.expired();
                            this.router.navigate(['/login']);
                        }
                    });
                }
            }
        });
    }

    async impersonateUser(userId: string): Promise<boolean> {
        const result = await firstValueFrom(
            this.authApiService.createActorToken(
                userId,
                this._sessionData.value.token
            )
        );

        if (result && result.message === 'Impersonation start!') {
            sessionStorage.setItem('shop_session', result.data.token);
            const session = new SessionData();
            session.token = result.data.token;
            session.stage = 'pending';
            const parsedData = jwtDecode(result.data.token) as any;
            session.user._id = parsedData._id;
            session.user.name = parsedData.name;
            session.user.email = parsedData.mail;
            session.user.roles = parsedData.roles;
            session.user.active = parsedData.active;
            session.tokenType = parsedData.type;
            session.user.expires = parsedData.expires;
            session.isLoggedIn = true;
            session.user.isSystemAdmin = parsedData.isSystemAdmin;
            session.actor = parsedData.actor;
            session.user.profile = parsedData.profile;
            this._sessionData.next(session);
            return true;
        }

        return false;
    }

    async endImpersonation(): Promise<boolean> {
        const result = await firstValueFrom(
            this.authApiService.revokeActorToken(this._sessionData.value.token)
        );

        if (result && result.message === 'Impersonation end!') {
            sessionStorage.setItem('shop_session', result.data.token);
            const session = new SessionData();
            session.token = result.data.token;
            session.stage = 'pending';
            const parsedData = jwtDecode(result.data.token) as any;
            session.user._id = parsedData._id;
            session.user.name = parsedData.name;
            session.user.email = parsedData.mail;
            session.user.roles = parsedData.roles;
            session.user.active = parsedData.active;
            session.tokenType = parsedData.type;
            session.user.expires = parsedData.expires;
            session.isLoggedIn = true;
            session.user.isSystemAdmin = parsedData.isSystemAdmin;
            session.user.profile = parsedData.profile;
            this._sessionData.next(session);
            return true;
        }

        return false;
    }

    getToken(): string {
        return this._sessionData.getValue().token;
    }
}
